import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from 'recharts';
import { Download } from 'lucide-react';
import { Button, Card, CardContent, CardHeader, CardTitle, Skeleton, chartA11yProps } from '@respectly/ui';
import type { AiUsageGroupBy } from '@respectly/shared-types';
import { useAiOpsStore } from '../../stores/useAiOpsStore';
import { BreakdownList } from './BreakdownList';
import { SERIES_COLORS, formatUsd } from './format';

const GROUP_OPTS: { key: AiUsageGroupBy; label: string }[] = [
  { key: 'category', label: 'Category' },
  { key: 'feature', label: 'Feature' },
  { key: 'agent', label: 'Agent' },
  { key: 'model', label: 'Model' },
];

export function UsageCostTab() {
  const { summary, timeseries, loadingSummary, loadOverview } = useAiOpsStore();
  const groupBy = timeseries?.groupBy ?? 'feature';
  const points = timeseries?.points ?? [];
  const keys = timeseries?.groupKeys ?? [];

  // Per-series totals over the window — the same numbers the stacked areas encode.
  const seriesTotals = keys.map((k) => ({
    key: k,
    total: points.reduce((sum, p) => sum + (p.series?.[k] ?? 0), 0),
  }));
  const windowTotal = seriesTotals.reduce((sum, s) => sum + s.total, 0);
  const chartLabel =
    `Stacked AI spend over time by ${groupBy}, ${points.length} day${points.length === 1 ? '' : 's'}, ` +
    `${formatUsd(windowTotal)} total: ${seriesTotals
      .map((s) => `${s.key} ${formatUsd(s.total)}`)
      .join(', ')}.`;

  const exportCsv = () => {
    if (!summary) return;
    const rows = [['Dimension', 'Name', 'Cost (USD)', 'Tokens', 'Calls', 'Share %']];
    const push = (dim: string, items: typeof summary.byFeature) =>
      items.forEach((i) => rows.push([dim, i.label, String(i.costUsd), String(i.totalTokens), String(i.calls), String(i.costShare)]));
    push('Category', summary.byCategory);
    push('Feature', summary.byFeature);
    push('Agent', summary.byAgent);
    push('Model', summary.byModel);
    push('Tenant', summary.topTenants);
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-usage_${summary.range.from}_${summary.range.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <Card className="border-slate-200 dark:border-slate-800">
        <CardHeader className="flex flex-row items-center justify-between gap-4 pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700 dark:text-slate-200">Spend over time</CardTitle>
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-slate-200 p-0.5 dark:border-slate-700">
              {GROUP_OPTS.map((g) => (
                <button
                  key={g.key}
                  onClick={() => void loadOverview(g.key)}
                  className={
                    'rounded-md px-2.5 py-1 text-xs font-medium transition-colors ' +
                    (groupBy === g.key
                      ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                      : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800')
                  }
                >
                  {g.label}
                </button>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={exportCsv}>
              <Download className="mr-1.5 h-3.5 w-3.5" /> CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingSummary && !timeseries ? (
            <Skeleton className="h-72 w-full" />
          ) : points.length === 0 ? (
            <div className="flex h-72 items-center justify-center text-sm text-slate-400">No AI calls in this range yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart
                data={points}
                margin={{ top: 10, right: 12, left: 0, bottom: 0 }}
                {...chartA11yProps(chartLabel)}
              >
                <defs>
                  {keys.map((k, i) => (
                    <linearGradient key={k} id={`g-${i}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={SERIES_COLORS[i % SERIES_COLORS.length]} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={SERIES_COLORS[i % SERIES_COLORS.length]} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748b' }} minTickGap={24} />
                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v) => formatUsd(Number(v))} width={64} />
                <RTooltip formatter={(v: number, name) => [formatUsd(v), name]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {keys.map((k, i) => (
                  <Area
                    key={k}
                    type="monotone"
                    name={k}
                    dataKey={(d: { series?: Record<string, number> }) => d.series?.[k] ?? 0}
                    stackId="1"
                    stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                    strokeWidth={1.5}
                    fill={`url(#g-${i})`}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <BreakdownList title="Cost by feature" items={summary?.byFeature ?? []} />
        <BreakdownList title="Cost by model" items={summary?.byModel ?? []} />
        <BreakdownList title="Cost by agent" items={summary?.byAgent ?? []} />
        <BreakdownList title="Cost by tenant" items={summary?.topTenants ?? []} emptyHint="Internal/ops usage has no tenant." />
      </div>
    </div>
  );
}
